import { InputSelectControlField, Flex } from '@sequencehq/core-components'
import type { ValidationError } from '@sequencehq/utils'
import { ConfigureXeroMappingsLink } from 'Products/components/ConfigureXeroMappingsLink'
import { NoXeroMappingsBanner } from 'Products/components/NoXeroMappingsBanner'
import {
  XERO_MAPPING_HELPER_TEXT,
  XERO_MAPPING_LABELS
} from 'Products/constants/xeroMapping.constants'

type XeroMappingField = {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  validationErrors?: ValidationError[]
  options?: { value: string; label: string; disabled?: boolean }[]
}

type XeroMappingFieldsProps = {
  deferredField: XeroMappingField
  recognizedField: XeroMappingField
  unbilledField: XeroMappingField
  hasMappings: boolean
  testIdPrefix: 'addProduct' | 'editProduct'
}

export const XeroMappingFields = ({
  deferredField,
  recognizedField,
  unbilledField,
  hasMappings,
  testIdPrefix
}: XeroMappingFieldsProps) => {
  if (!hasMappings) {
    return (
      <NoXeroMappingsBanner
        data-testid={`${testIdPrefix}.noXeroMappingsBanner`}
      />
    )
  }

  const fields = [
    {
      field: deferredField,
      label: XERO_MAPPING_LABELS.DEFERRED_REVENUE,
      helperText: XERO_MAPPING_HELPER_TEXT.DEFERRED_REVENUE,
      testId: `${testIdPrefix}.deferredRevenueXeroAccount`
    },
    {
      field: recognizedField,
      label: XERO_MAPPING_LABELS.RECOGNIZED_REVENUE,
      helperText: XERO_MAPPING_HELPER_TEXT.RECOGNIZED_REVENUE,
      testId: `${testIdPrefix}.recognizedRevenueXeroAccount`
    },
    {
      field: unbilledField,
      label: XERO_MAPPING_LABELS.UNBILLED_REVENUE,
      helperText: XERO_MAPPING_HELPER_TEXT.UNBILLED_REVENUE,
      testId: `${testIdPrefix}.unbilledRevenueXeroAccount`
    }
  ]

  return (
    <Flex
      data-testid={`${testIdPrefix}.xeroMappings`}
      flexDirection="column"
      gap="16px"
    >
      {fields.map(({ field, label, helperText, testId }) => (
        <InputSelectControlField
          key={testId}
          label={label}
          data-testid={testId}
          placeholder="Select account"
          tooltip={{ title: label, content: helperText }}
          disabled={field.disabled}
          validationErrors={field.validationErrors}
          options={field.options ?? []}
          initialValue={field.value}
          onChange={(value: string) => {
            field.onChange(value)
          }}
        />
      ))}
      <ConfigureXeroMappingsLink
        data-testid={`${testIdPrefix}.configureXeroMappingsLink`}
      />
    </Flex>
  )
}
